import { callable } from '@/services/api/backend';
import type { CartLine, MarketplaceOrder } from '@/types';

/**
 * Real marketplace purchase path. The server re-prices every line from the
 * seller's storefront listing, so the client only sends what it wants to buy.
 */

export type MarketplacePaymentMethod = 'card' | 'telebirr';

export interface MarketplacePaymentIntent {
  transactionId: string;
  clientSecret: string;
  amount: number;
  currency: 'usd' | 'etb';
  telebirrUrl?: string;
  /** Server-computed tax for the buyer's address, in the same currency as `amount`'s units. */
  tax?: { totalTax: number };
}

/** Tax is calculated from the buyer's country (ISO 3166-1 alpha-2) and optional state/region. */
export interface TaxAddress { country: string; state?: string }

const PENDING_KEY = 'endeeko.marketplace.pendingPayment';

const toLines = (lines: CartLine[]) => lines.map((l) => ({
  companyId: l.product.companyId,
  listingId: l.product.id,
  quantity: l.quantity,
  ...(l.variant ? { variantName: l.variant.name } : {}),
}));

export const marketplaceCheckoutService = {
  createPayment(lines: CartLine[], paymentMethod: MarketplacePaymentMethod, returnUrl?: string, address?: TaxAddress): Promise<MarketplacePaymentIntent> {
    return callable<MarketplacePaymentIntent>('createMarketplacePayment', {
      lines: toLines(lines),
      paymentMethod,
      ...(returnUrl ? { returnUrl } : {}),
      ...(address ? { address } : {}),
    });
  },

  /** Resolves true once the webhook has marked the transaction succeeded; false if it failed or never settled. */
  async waitForPayment(transactionId: string, attempts = 20): Promise<boolean> {
    for (let i = 0; i < attempts; i++) {
      try {
        const { status } = await callable<{ status: string }>('getPaymentStatus', { transactionId });
        if (status === 'succeeded') return true;
        if (status === 'failed' || status === 'not_found') return false;
      } catch { /* transient; keep polling */ }
      await new Promise((r) => setTimeout(r, 1500));
    }
    return false;
  },

  listOrders(): Promise<MarketplaceOrder[]> {
    return callable<{ orders: MarketplaceOrder[] }>('getMarketplaceOrders', {}).then((r) => r.orders);
  },

  createOrder(transactionId: string): Promise<{ orderId: string }> {
    return callable<{ orderId: string }>('createMarketplaceOrder', { transactionId });
  },

  /** Remembers an in-flight Telebirr payment so the return page can finish the order. */
  rememberPending(transactionId: string) {
    try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ transactionId })); } catch { /* ignore */ }
  },
  takePending(): string | null {
    try {
      const raw = sessionStorage.getItem(PENDING_KEY);
      sessionStorage.removeItem(PENDING_KEY);
      return raw ? (JSON.parse(raw) as { transactionId: string }).transactionId : null;
    } catch {
      return null;
    }
  },
};
